
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import PatientFeedback from "./pages/PatientFeedback";
import PatientRegistration from "./pages/PatientRegistration";

const NAV_ITEMS = [
  {
    id: "registration",
    label: "Patient Registration",
    icon: "＋",
    description: "Register a new patient",
  },
  {
    id: "feedback",
    label: "Patient Feedback",
    icon: "♡",
    description: "Share your experience",
  },
];

export default function PatientFormsApp() {
  const [activePage, setActivePage] = useState("registration");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();

  const activeItem = NAV_ITEMS.find(
    (item) => item.id === activePage
  );

  const selectPage = (page) => {
    setActivePage(page);
    setSidebarOpen(false);
  };

  return (
    <div className="min-h-screen bg-[#F4F7FC] text-slate-800">

      {/* Mobile Header */}
      <header className="sticky top-0 z-40 flex h-[72px] items-center justify-between border-b border-slate-200 bg-white px-4 shadow-sm md:hidden">

        <div className="flex min-w-0 items-center gap-3">
          <img
            src="/VPJCLogo-New.png"
            alt="VPJC Logo"
            className="h-11 w-11 shrink-0 object-contain"
          />

          <div className="min-w-0">
            <h1 className="truncate text-base font-bold tracking-tight text-[#204080]">
              VPJC
            </h1>
            <p className="truncate text-[11px] text-slate-500">
              Patient Portal
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          aria-label={sidebarOpen ? "Close menu" : "Open menu"}
          aria-expanded={sidebarOpen}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 text-xl text-[#204080] transition hover:bg-blue-50"
        >
          {sidebarOpen ? "✕" : "☰"}
        </button>
      </header>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <button
          type="button"
          aria-label="Close navigation"
          onClick={() => setSidebarOpen(false)}
          className="fixed inset-0 z-40 bg-slate-900/40 md:hidden"
        />
      )}

      <div className="flex min-h-[calc(100vh-72px)] md:min-h-screen">

        {/* Sidebar */}
        <aside
          className={`
            fixed inset-y-0 left-0 z-50 flex w-[280px] flex-col
            border-r border-slate-200 bg-white
            transition-transform duration-300
            md:sticky md:top-0 md:h-screen md:w-[250px]
            md:translate-x-0 lg:w-[270px]
            ${
              sidebarOpen
                ? "translate-x-0"
                : "-translate-x-full md:translate-x-0"
            }
          `}
        >

          {/* Desktop Logo */}
          <div className="hidden border-b border-slate-100 px-6 py-7 md:block">
            <div className="flex items-center gap-3">
              <img
                src="/VPJCLogo-New.png"
                alt="VPJC Logo"
                className="h-14 w-14 shrink-0 object-contain"
              />

              <div className="min-w-0">
                <h1 className="text-xl font-extrabold tracking-wide text-[#204080]">
                  VPJC
                </h1>
                <p className="text-[11px] font-medium text-slate-500">
                  Patient Management
                </p>
              </div>
            </div>

            <div className="mt-5 rounded-xl bg-[#F0F5FF] px-3 py-3">
              <p className="text-xs font-semibold text-[#204080]">
                VISAKHA PHYSIOTHERAPY
              </p>
              <p className="mt-1 text-[10px] text-slate-500">
                & Jasti Chiropractic Centre
              </p>
            </div>
          </div>

          {/* Mobile Drawer Branding */}
          <div className="flex items-center justify-between border-b border-slate-100 px-5 py-5 md:hidden">
            <div className="flex items-center gap-3">
              <img
                src="/VPJCLogo-New.png"
                alt="VPJC Logo"
                className="h-12 w-12 object-contain"
              />
              <div>
                <h2 className="font-bold text-[#204080]">VPJC</h2>
                <p className="text-xs text-slate-500">Patient Portal</p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setSidebarOpen(false)}
              className="rounded-lg px-2 py-1 text-lg text-slate-400 hover:bg-slate-100"
              aria-label="Close menu"
            >
              ✕
            </button>
          </div>

          {/* Navigation */}
          <nav
            aria-label="Patient forms navigation"
            className="flex-1 space-y-2 px-4 py-6"
          >
            <p className="mb-4 px-3 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
              Workspace
            </p>

            {NAV_ITEMS.map((item) => {
              const isActive = activePage === item.id;

              return (
                <button
                  key={item.id}
                  type="button"
                  aria-current={isActive ? "page" : undefined}
                  onClick={() => selectPage(item.id)}
                  className={`
                    group flex min-h-[66px] w-full items-center gap-3
                    rounded-xl px-3 py-3 text-left transition-all duration-200
                    ${
                      isActive
                        ? "bg-[#204080] text-white shadow-md shadow-blue-900/10"
                        : "text-slate-600 hover:bg-[#F1F5FC] hover:text-[#204080]"
                    }
                  `}
                >
                  <span
                    className={`
                      flex h-10 w-10 shrink-0 items-center justify-center
                      rounded-xl text-xl transition
                      ${
                        isActive
                          ? "bg-white/15 text-white"
                          : "bg-[#F0F4FA] text-[#204080] group-hover:bg-white"
                      }
                    `}
                    aria-hidden="true"
                  >
                    {item.icon}
                  </span>

                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold">
                      {item.label}
                    </span>
                    <span
                      className={`mt-1 block text-[11px] ${
                        isActive ? "text-blue-100" : "text-slate-400"
                      }`}
                    >
                      {item.description}
                    </span>
                  </span>

                  {isActive && (
                    <span className="text-lg text-blue-100">›</span>
                  )}
                </button>
              );
            })}
          </nav>

          {/* Sidebar Bottom */}
          <div className="border-t border-slate-100 p-4">
            <div className="mb-3 rounded-xl bg-[#F5F8FD] p-3">
              <p className="text-xs font-semibold text-[#204080]">
                Patient Care Matters
              </p>
              <p className="mt-1 text-[11px] leading-relaxed text-slate-500">
                Thank you for choosing VPJC for your healthcare needs.
              </p>
            </div>

            <button
              type="button"
              onClick={() => navigate("/")}
              className="flex min-h-11 w-full items-center gap-3 rounded-xl px-3 text-sm font-medium text-slate-600 transition hover:bg-slate-100 hover:text-[#204080]"
            >
              <span className="text-lg">←</span>
              Back to VPJC Website
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <div className="min-w-0 flex-1">

          {/* Desktop Topbar */}
          <div className="hidden h-[76px] items-center justify-between border-b border-slate-200 bg-white px-6 lg:px-10 md:flex">
            <div>
              <p className="text-xs font-medium text-slate-400">
                VPJC / Patient Portal
              </p>
              <h2 className="mt-1 text-lg font-bold text-[#204080]">
                {activeItem?.label}
              </h2>
            </div>

            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className="text-sm font-semibold text-slate-700">
                  VPJC Patient Services
                </p>
                <p className="text-xs text-slate-400">
                  Visakha Physiotherapy & Jasti Chiropractic Centre
                </p>
              </div>

              <img
                src="/VPJCLogo-New.png"
                alt="VPJC"
                className="h-10 w-10 object-contain"
              />
            </div>
          </div>

          {/* Page Heading */}
          <div className="px-4 pt-6 sm:px-6 sm:pt-8 lg:px-10">
            <div className="mb-6">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#5275AA]">
                Patient Services
              </p>

              <h1 className="mt-2 text-2xl font-bold tracking-tight text-[#204080] sm:text-3xl">
                {activeItem?.label}
              </h1>

              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-500">
                {activePage === "registration"
                  ? "Enter patient details to create a new registration."
                  : "We value your feedback. Share your experience with our care and services."}
              </p>
            </div>
          </div>

          {/* Form Content */}
          <main className="px-3 pb-8 sm:px-6 lg:px-10">
            <div className="mx-auto w-full max-w-5xl">
              {activePage === "registration" ? (
                <PatientRegistration />
              ) : (
                <PatientFeedback />
              )}
            </div>
          </main>

          {/* Footer */}
          <footer className="border-t border-slate-200 bg-white px-4 py-5 text-center sm:px-6">
            <p className="text-xs text-slate-400">
              © {new Date().getFullYear()} Visakha Physiotherapy & Jasti
              Chiropractic Centre. All rights reserved.
            </p>
          </footer>
        </div>
      </div>
    </div>
  );
}
